import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FaCopy, FaPuzzlePiece, FaSave, FaTicketAlt } from 'react-icons/fa';
import { useApp } from '../../context/AppContext';
import AdminSettingsBackLink from '../../components/admin/AdminSettingsBackLink';
import {
  buildWidgetSnippet,
  getAdminWidgetConfigs,
  getApiOrigin,
  upsertWidgetConfig,
} from '../../services/widgetConfigService';
import EmbedMyTicketsPanel from '../embed/EmbedMyTicketsPanel';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;
  background-color: var(--color-bg-secondary);
  min-height: 100vh;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(280px, 0.9fr);
  gap: 1.25rem;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.section`
  background: var(--color-bg-primary);
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid var(--color-border-primary);
  box-shadow: var(--shadow-sm);
  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const PageTitle = styled.h1`
  margin: 0 0 0.5rem;
  font-size: 1.5rem;
  font-weight: 650;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: var(--color-text-primary);
`;

const Hint = styled.p`
  margin: 0 0 1.25rem;
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  line-height: 1.5;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FieldLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--color-text-secondary);
`;

const inputStyles = `
  width: 100%;
  box-sizing: border-box;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--color-border-primary);
  border-radius: 10px;
  font-size: 0.9rem;
  font-family: inherit;
  color: var(--color-text-primary);
  background: var(--color-bg-primary);

  &:focus {
    outline: none;
    border-color: var(--color-secondary);
    box-shadow: 0 0 0 2px rgba(178, 190, 195, 0.35);
  }
`;

const StyledInput = styled.input`
  ${inputStyles}
`;

const StyledSelect = styled.select`
  ${inputStyles}
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1rem;
  border: 1px solid ${({ $variant }) =>
    $variant === 'secondary' ? 'var(--color-border-primary)' : 'var(--color-btn-primary-border)'};
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  background: ${({ $variant }) => ($variant === 'secondary' ? 'var(--color-bg-tertiary)' : 'var(--color-btn-primary-bg)')};
  color: ${({ $variant }) => ($variant === 'secondary' ? 'var(--color-text-primary)' : 'var(--color-btn-primary-text)')};
  box-sizing: border-box;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ActionsWrap = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const PreviewShell = styled.div`
  position: relative;
  min-height: 320px;
  border-radius: 12px;
  border: 1px dashed var(--color-border-primary);
  background: var(--color-bg-tertiary);
`;

const PreviewChrome = styled.div`
  padding: 0.65rem 0.85rem;
  font-size: 0.75rem;
  color: var(--color-text-secondary);
  border-bottom: 1px solid var(--color-border-primary);
  background: var(--color-bg-primary);
`;

const PreviewBody = styled.div`
  position: relative;
  min-height: 360px;
  padding: 1rem;
`;

const PreviewSlot = styled.div`
  width: 100%;
  min-height: 240px;
  border-radius: 10px;
  background: var(--color-bg-primary);
  box-shadow: inset 0 0 0 1px var(--color-border-primary);
  display: flex;
  align-items: stretch;
  justify-content: stretch;
`;

const TriggerButton = styled.button`
  margin: auto;
  border: none;
  border-radius: 999px;
  width: 3.25rem;
  height: 3.25rem;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  cursor: pointer;
  color: ${({ $text }) => $text || '#fff'};
  background: ${({ $bg }) => $bg || '#2d3436'};
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
`;

const LivePreview = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

/** Só no admin: limita a altura do preview sem afetar o widget real no host. */
const PreviewViewport = styled.div`
  width: 100%;
  max-height: 420px;
  overflow: auto;
  border-radius: 10px;
`;

const SnippetBox = styled.pre`
  margin: 0;
  padding: 1rem;
  background: var(--color-bg-tertiary);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border-primary);
  border-radius: 12px;
  font-size: 0.72rem;
  line-height: 1.45;
  overflow: auto;
  max-height: 280px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const DocsList = styled.ol`
  margin: 0;
  padding-left: 1.2rem;
  color: var(--color-text-secondary);
  font-size: 0.85rem;
  line-height: 1.55;
`;

const emptyForm = {
  appOriginCode: '',
  backgroundColor: '#2D3436',
  textColor: '#FFFFFF',
};

const WidgetsAdminPage = () => {
  const { addToast } = useApp();
  const [configs, setConfigs] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminWidgetConfigs();
      setConfigs(Array.isArray(data) ? data : []);
      if (Array.isArray(data) && data.length > 0) {
        setForm((prev) => {
          const selected =
            data.find((item) => item.appOriginCode === prev.appOriginCode) || data[0];
          return {
            appOriginCode: selected.appOriginCode,
            backgroundColor: selected.backgroundColor || '#2D3436',
            textColor: selected.textColor || '#FFFFFF',
          };
        });
      }
    } catch (error) {
      console.error(error);
      addToast?.('Não foi possível carregar as configs de widget.', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    load();
  }, [load]);

  const snippet = useMemo(
    () =>
      buildWidgetSnippet({
        appOriginCode: form.appOriginCode || 'SEATHUB',
        apiBaseUrl: getApiOrigin(),
        embedBaseUrl: typeof window !== 'undefined' ? window.location.origin : '',
      }),
    [form.appOriginCode]
  );

  const onSelectOrigin = (code) => {
    const selected = configs.find((item) => item.appOriginCode === code);
    if (!selected) return;
    setForm({
      appOriginCode: selected.appOriginCode,
      backgroundColor: selected.backgroundColor || '#2D3436',
      textColor: selected.textColor || '#FFFFFF',
    });
    setPreviewOpen(false);
  };

  const onSave = async () => {
    if (!form.appOriginCode) return;
    setSaving(true);
    try {
      const saved = await upsertWidgetConfig(form.appOriginCode, {
        backgroundColor: form.backgroundColor,
        textColor: form.textColor,
      });
      setConfigs((prev) => {
        const idx = prev.findIndex((item) => item.appOriginCode === saved.appOriginCode);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...prev[idx], ...saved };
          return next;
        }
        return [...prev, saved];
      });
      setForm({
        appOriginCode: saved.appOriginCode,
        backgroundColor: saved.backgroundColor,
        textColor: saved.textColor,
      });
      addToast?.('Widget salvo.', 'success');
    } catch (error) {
      console.error(error);
      addToast?.(error?.response?.data?.message || 'Falha ao salvar widget.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const copySnippet = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      addToast?.('Snippet copiado.', 'success');
    } catch (error) {
      console.error(error);
      addToast?.('Não foi possível copiar o snippet.', 'error');
    }
  };

  return (
    <PageContainer>
      <AdminSettingsBackLink />
      <Panel>
        <PageTitle>
          <FaPuzzlePiece style={{ marginRight: '0.5rem' }} />
          Widgets
        </PageTitle>
        <Hint>
          Configure o botão embutido por aplicação de origem. O app destino monta o widget numa{' '}
          <code>div</code>; ao abrir, a lista de Meus tickets preenche o espaço via iframe. O token
          de sessão é pedido pelo backend do host (client credentials + e-mail) a cada abertura.
        </Hint>

        {loading ? (
          <p>Carregando...</p>
        ) : (
          <Layout>
            <div>
              <FormGrid>
                <FieldLabel>
                  Aplicação (origem)
                  <StyledSelect
                    value={form.appOriginCode}
                    onChange={(e) => onSelectOrigin(e.target.value)}
                  >
                    {configs.map((item) => (
                      <option key={item.appOriginCode} value={item.appOriginCode}>
                        {item.appOriginCode}
                      </option>
                    ))}
                  </StyledSelect>
                </FieldLabel>
                <FieldLabel>
                  Cor de fundo
                  <StyledInput
                    type="color"
                    value={form.backgroundColor || '#2D3436'}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        backgroundColor: e.target.value.toUpperCase(),
                      }))
                    }
                  />
                </FieldLabel>
                <FieldLabel>
                  Cor do ícone
                  <StyledInput
                    type="color"
                    value={form.textColor || '#FFFFFF'}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, textColor: e.target.value.toUpperCase() }))
                    }
                  />
                </FieldLabel>
              </FormGrid>

              <ActionsWrap>
                <Button type="button" onClick={onSave} disabled={saving || !form.appOriginCode}>
                  <FaSave />
                  {saving ? 'Salvando...' : 'Salvar'}
                </Button>
                <Button type="button" $variant="secondary" onClick={() => setPreviewOpen((v) => !v)}>
                  {previewOpen ? 'Ver fechado' : 'Ver aberto'}
                </Button>
                <Button type="button" $variant="secondary" onClick={copySnippet}>
                  <FaCopy />
                  Copiar snippet
                </Button>
              </ActionsWrap>

              <Hint style={{ marginTop: '1.25rem', marginBottom: '0.75rem' }}>
                Contrato do BFF do app host
              </Hint>
              <DocsList>
                <li>
                  Obter token M2M com <code>client_id</code> / <code>client_secret</code>{' '}
                  (client_credentials).
                </li>
                <li>
                  <code>POST /api/private/widgets/embed-session</code> com Bearer M2M e body{' '}
                  <code>{`{ "appOriginCode": "${form.appOriginCode || 'SEATHUB'}", "email": "user@..." }`}</code>
                  .
                </li>
                <li>
                  Devolver o <code>embedToken</code> ao frontend do host; o <code>widget.js</code>{' '}
                  usa esse token no iframe (via postMessage), sem colocá-lo na URL.
                </li>
              </DocsList>
            </div>

            <div>
              <Hint style={{ marginBottom: '0.75rem' }}>Preview no espaço do host</Hint>
              <PreviewShell>
                <PreviewChrome>App destino · slot do widget</PreviewChrome>
                <PreviewBody>
                  <PreviewSlot>
                    {!previewOpen ? (
                      <TriggerButton
                        type="button"
                        $bg={form.backgroundColor}
                        $text={form.textColor}
                        onClick={() => setPreviewOpen(true)}
                        aria-label="Tickets"
                      >
                        <FaTicketAlt aria-hidden />
                      </TriggerButton>
                    ) : (
                      <PreviewViewport>
                        <LivePreview>
                          <EmbedMyTicketsPanel onClose={() => setPreviewOpen(false)} />
                        </LivePreview>
                      </PreviewViewport>
                    )}
                  </PreviewSlot>
                </PreviewBody>
              </PreviewShell>

              <Hint style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>Snippet de integração</Hint>
              <SnippetBox>{snippet}</SnippetBox>
            </div>
          </Layout>
        )}
      </Panel>
    </PageContainer>
  );
};

export default WidgetsAdminPage;
